import { Alert, Button, NumberInput, Select } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { IconAlertTriangle, IconCurrencyEuro } from '@tabler/icons-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import {
  ReimbursableExpenseOption,
  ReimbursableExpensesPicker,
} from 'src/Components/Forms/MoneyTransfer/ReimbursableExpensesPicker';
import useFetchBankAccounts from 'src/Hooks/Queries/useFetchBankAccounts';
import useFetchExpenses from 'src/Hooks/Queries/useFetchExpenses';
import useFetchMembers from 'src/Hooks/Queries/useFetchMembers';
import { CreateMoneyTransferDto } from 'src/types/money-transfer.types';
import {
  getExpectedAmountForExpense,
  getExpectedTotal,
  hasMixedReimbursementCases,
  isAmountMatchingSelection,
  isReimbursableBy,
  isSenderParticipant,
  parseTransferParty,
} from 'src/Utils/reimbursement';

import styles from './MoneyTransferForm.module.scss';

interface Props {
  onSubmit: (values: CreateMoneyTransferDto) => void;
  onCancel: () => void;
  isLoading?: boolean;
  /** Reason the API refused the last submission, shown above the buttons. */
  submitError?: string | null;
  /**
   * Valeurs de départ en édition. En création elles viennent du `location.state`, qui reste
   * le chemin des liens pré-remplis (« Enregistrer un remboursement » depuis un solde).
   */
  initialValues?: Partial<FormValues>;
  /**
   * En édition, les deux parties ne se changent pas : l'API n'accepte que le montant, la date
   * et la liste des dépenses. Les rendre modifiables promettrait ce qu'elle refuse.
   */
  lockParties?: boolean;
  submitLabel?: string;
}

interface FormValues {
  amount: number;
  date: Date;
  source: string | null;
  destination: string | null;
  expenseIds: string[];
}

export const MoneyTransferForm = ({
  onSubmit,
  onCancel,
  isLoading,
  submitError,
  initialValues,
  lockParties = false,
  submitLabel,
}: Props) => {
  const { t } = useTranslation();
  const { data: membersData } = useFetchMembers();
  const { data: bankAccounts } = useFetchBankAccounts();
  // No year: an expense still waiting to be paid back is very often last year's.
  const { data: expenses, isLoading: expensesLoading } = useFetchExpenses();
  const location = useLocation();
  const state = location.state as {
    amount?: number;
    toUserId?: string;
    toBankAccountId?: string;
    fromUserId?: string;
    fromBankAccountId?: string;
    fromPlaceholderMemberId?: string;
    toPlaceholderMemberId?: string;
    /** Set when opened from a fund call line, so recording the payment settles that line. */
    fundCallLineId?: string;
  };

  const form = useForm<FormValues>({
    initialValues: {
      amount: state?.amount ?? 0,
      date: new Date(),
      source: state?.fromUserId
        ? `user-${state.fromUserId}`
        : state?.fromPlaceholderMemberId
          ? `placeholder-${state.fromPlaceholderMemberId}`
          : state?.fromBankAccountId
            ? `bank-${state.fromBankAccountId}`
            : null,
      destination: state?.toUserId
        ? `user-${state.toUserId}`
        : state?.toPlaceholderMemberId
          ? `placeholder-${state.toPlaceholderMemberId}`
          : state?.toBankAccountId
            ? `bank-${state.toBankAccountId}`
            : null,
      expenseIds: [],
      ...initialValues,
    },
    validate: {
      amount: value => (value > 0 ? null : t('form.errors.amount', 'Le montant doit être positif')),
      date: value => (value ? null : t('form.errors.required', 'Requis')),
      source: value => (value ? null : t('form.errors.required', 'Requis')),
      destination: value => (value ? null : t('form.errors.required', 'Requis')),
    },
  });

  const options = useMemo(() => {
    // member.user.id carries a PlaceholderMember id for unregistered co-owners
    // (isPlaceholder), hence the distinct prefix so it routes to *PlaceholderMemberId.
    const memberOptions = (membersData?.members ?? []).map(member => {
      const name = `${member.user.firstName} ${member.user.lastName}`;
      return {
        label: member.isPlaceholder
          ? t('admin.households.form.headNotRegisteredOption', '{{name}} (non inscrit)', { name })
          : name,
        value: member.isPlaceholder ? `placeholder-${member.user.id}` : `user-${member.user.id}`,
      };
    });

    const bankAccountOptions = (bankAccounts ?? []).map(account => ({
      label: account.name,
      value: `bank-${account.id}`,
    }));

    const opts = [];
    if (memberOptions.length > 0) {
      opts.push({ group: t('expense.form.members', 'Membres'), items: memberOptions });
    }
    if (bankAccountOptions.length > 0) {
      opts.push({
        group: t('expense.form.bankAccounts', 'Comptes bancaires'),
        items: bankAccountOptions,
      });
    }
    return opts;
  }, [membersData, bankAccounts, t]);

  const getPartyLabel = (value: string | null): string =>
    options.flatMap(group => group.items).find(item => item.value === value)?.label ?? '';

  const sender = useMemo(() => parseTransferParty(form.values.source), [form.values.source]);
  const beneficiary = useMemo(
    () => parseTransferParty(form.values.destination),
    [form.values.destination]
  );

  // Only the beneficiary's own unpaid expenses can be linked: the API refuses a link where
  // they are not the payer.
  const reimbursableExpenses = useMemo(
    () => (beneficiary ? expenses.filter(expense => isReimbursableBy(expense, beneficiary)) : []),
    [expenses, beneficiary]
  );

  const selectedExpenses = useMemo(
    () => reimbursableExpenses.filter(expense => form.values.expenseIds.includes(expense.id)),
    [reimbursableExpenses, form.values.expenseIds]
  );

  const pickerOptions: ReimbursableExpenseOption[] = useMemo(
    () =>
      reimbursableExpenses.map(expense => ({
        id: expense.id,
        title: expense.title,
        date: expense.date.toDate(),
        totalAmount: expense.amount,
        expectedAmount: getExpectedAmountForExpense(expense, sender),
        isShareOnly: isSenderParticipant(expense, sender),
        settledSharesCount: expense.settledSharesCount,
        totalSharesCount: expense.totalSharesCount,
      })),
    [reimbursableExpenses, sender]
  );

  const expectedTotal = getExpectedTotal(selectedExpenses, sender);
  const isMixedCase = hasMixedReimbursementCases(selectedExpenses, sender);
  const isParticipantCase = selectedExpenses.some(expense => isSenderParticipant(expense, sender));
  const hasAmountMismatch =
    selectedExpenses.length > 0 &&
    !isMixedCase &&
    !isAmountMatchingSelection(form.values.amount, expectedTotal);

  const mixedCaseMessage = t(
    'moneyTransfer.form.mixedCaseError',
    'Un même remboursement ne peut pas mêler des dépenses où vous participez et des dépenses où vous ne participez pas. Enregistrez-les séparément.'
  );

  const amountMismatchMessage = t(
    'moneyTransfer.form.amountMustMatchSelection',
    'Le montant doit être égal à {{amount}} €, ce que soldent les dépenses cochées.',
    { amount: expectedTotal.toFixed(2) }
  );

  const mixedCaseError = isMixedCase ? mixedCaseMessage : null;
  const amountMismatchError = hasAmountMismatch ? amountMismatchMessage : null;

  const selectionNotice = (() => {
    if (selectedExpenses.length === 0 || isMixedCase) return null;
    return isParticipantCase
      ? t(
          'moneyTransfer.form.noticeParticipant',
          'Seule votre part sera réglée : les autres participants resteront redevables envers {{payer}}.',
          { payer: getPartyLabel(form.values.destination) }
        )
      : t(
          'moneyTransfer.form.noticeClaimMoved',
          'Les participants devront désormais leur part à {{sender}}.',
          { sender: getPartyLabel(form.values.source) }
        );
  })();

  /** Keeps the amount on what the checked expenses actually settle, to the cent. */
  const applySelection = (expenseIds: string[], sourceValue: string | null) => {
    form.setFieldValue('expenseIds', expenseIds);
    form.clearFieldError('expenseIds');
    if (expenseIds.length === 0) return;
    const nextSender = parseTransferParty(sourceValue);
    const nextSelection = reimbursableExpenses.filter(expense => expenseIds.includes(expense.id));
    form.setFieldValue('amount', getExpectedTotal(nextSelection, nextSender));
    form.clearFieldError('amount');
  };

  const handleSubmit = (values: FormValues) => {
    if (values.expenseIds.length > 0) {
      if (isMixedCase) {
        form.setFieldError('expenseIds', mixedCaseMessage);
        return;
      }
      if (hasAmountMismatch) {
        form.setFieldError('amount', amountMismatchMessage);
        return;
      }
    }

    const payload: CreateMoneyTransferDto = {
      amount: values.amount,
      date: values.date.toISOString(),
    };

    if (values.source?.startsWith('user-')) {
      payload.fromUserId = values.source.replace('user-', '');
    } else if (values.source?.startsWith('placeholder-')) {
      payload.fromPlaceholderMemberId = values.source.replace('placeholder-', '');
    } else if (values.source?.startsWith('bank-')) {
      payload.fromBankAccountId = values.source.replace('bank-', '');
    }

    if (values.destination?.startsWith('user-')) {
      payload.toUserId = values.destination.replace('user-', '');
    } else if (values.destination?.startsWith('placeholder-')) {
      payload.toPlaceholderMemberId = values.destination.replace('placeholder-', '');
    } else if (values.destination?.startsWith('bank-')) {
      payload.toBankAccountId = values.destination.replace('bank-', '');
    }

    if (values.expenseIds.length > 0) {
      payload.expenseIds = values.expenseIds;
    }

    if (state?.fundCallLineId) {
      payload.fundCallLineId = state.fundCallLineId;
    }

    onSubmit(payload);
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)} className={styles.container}>
      <NumberInput
        label={t('moneyTransfer.form.amount', 'Montant')}
        placeholder="0,00"
        rightSection={<IconCurrencyEuro size={20} />}
        min={0}
        decimalScale={2}
        decimalSeparator=","
        thousandSeparator=" "
        {...form.getInputProps('amount')}
        error={amountMismatchError ?? form.errors.amount}
      />

      <DatePickerInput
        label={t('moneyTransfer.form.date', 'Date')}
        valueFormat="D MMMM YYYY"
        {...form.getInputProps('date')}
      />

      <Select
        label={t('moneyTransfer.form.from', 'De')}
        placeholder={t('moneyTransfer.form.selectSource', 'Sélectionner un émetteur')}
        data={options}
        searchable
        disabled={lockParties}
        {...form.getInputProps('source')}
        onChange={value => {
          form.setFieldValue('source', value);
          // The sender decides whether a selection settles a whole expense or only their
          // own share, so the amount is recomputed rather than left on the previous case.
          applySelection(form.values.expenseIds, value);
        }}
      />

      <Select
        label={t('moneyTransfer.form.to', 'Vers')}
        placeholder={t('moneyTransfer.form.selectDestination', 'Sélectionner un bénéficiaire')}
        data={options}
        searchable
        disabled={lockParties}
        {...form.getInputProps('destination')}
        onChange={value => {
          form.setFieldValue('destination', value);
          // Eligibility is tied to the beneficiary being the payer: another one, another list.
          form.setFieldValue('expenseIds', []);
          form.clearFieldError('expenseIds');
        }}
      />

      {beneficiary && (
        <ReimbursableExpensesPicker
          options={pickerOptions}
          value={form.values.expenseIds}
          onChange={expenseIds => applySelection(expenseIds, form.values.source)}
          loading={expensesLoading}
          error={mixedCaseError ?? form.errors.expenseIds}
          notice={selectionNotice}
        />
      )}

      {submitError && (
        <Alert color="red" variant="light" icon={<IconAlertTriangle size={18} />}>
          {submitError}
        </Alert>
      )}

      <div className={styles.buttonsContainer}>
        <Button variant="outline" onClick={onCancel} disabled={isLoading}>
          {t('common.cancel', 'Annuler')}
        </Button>
        <Button type="submit" loading={isLoading}>
          {submitLabel ?? t('common.save', 'Enregistrer')}
        </Button>
      </div>
    </form>
  );
};
